/**
 * The 2029–2030 life-area horoscope tail was de-indexed by D1 migration 0030
 * (forward-dated 3–4 years, structurally ~zero demand) and its rows were later
 * pruned, so those URLs now 404 while published `/yearly-rashifal/*` pages still
 * link to every year in the series. Send each retired year to the surviving
 * near-term 2028 page for the same rashi rather than leaving a dead link — the
 * same treatment the retired product pages got on 2026-07-24.
 *
 * CALIBRATED 2026-07-26: all four types × mesh/dhanu/meen `-2028/` verified 200
 * live before landing; 2026–2028 remain indexed per migration 0030.
 */
const RETIRED_LIFE_AREA_HOROSCOPE_YEARS: Readonly<Record<string, string>> = Object.fromEntries(
  ["career", "love", "marriage", "health"].flatMap((area) =>
    [
      "mesh",
      "vrishabh",
      "mithun",
      "kark",
      "singh",
      "kanya",
      "tula",
      "vrishchik",
      "dhanu",
      "makar",
      "kumbh",
      "meen",
    ].flatMap((rashi) =>
      [2029, 2030].map((year) => [
        `/${area}-horoscope/${rashi}-${year}/`,
        `/${area}-horoscope/${rashi}-2028/`,
      ])
    )
  )
);

/** Canonical path-changing redirects shared by middleware and revenue attribution. */
export const LEGACY_CRAWL_CANONICAL_PATHS: Readonly<Record<string, string>> = {
  ...RETIRED_LIFE_AREA_HOROSCOPE_YEARS,
  "/free-astrology-chat/": "/ai-astrologer/",
  "/membership/": "/ai-astrologer/",
  "/matching/": "/kundli-matching/",
  // Consolidated 2026-07-22: thin /prashna/ guide → the /prashna-kundali/ tool
  // (kundli-matching-style cannibalization; the tool page dominates the query).
  "/prashna/": "/prashna-kundali/",
  "/kundali/": "/free-kundali/",
  "/daily-rashifal/": "/rashifal/",
  "/today-rashifal/": "/rashifal/",
  "/pages/about/": "/about/",
  "/pages/about-us/": "/about/",
  "/pages/contact/": "/contact/",
  "/pages/contact-us/": "/contact/",
  "/policies/privacy-policy/": "/privacy/",
  "/policies/shipping-policy/": "/shipping-policy/",
  "/rashi/": "/rashi/mesh/",
  "/remedies/": "/upay/",
  "/hi/remedies/": "/hi/upay/",
  "/dosha/kaal-sarp-dosha/": "/kaal-sarp-dosha/",
  "/dosha/mangal-dosha/": "/mangal-dosha-calculator/",
  "/mangal-dosha/": "/mangal-dosha-calculator/",
  "/dosha/pitra-dosha/": "/pitra-dosha/",
  "/dosha/shani-dosha/": "/sade-sati/",
  "/dosha/rahu-dosha/": "/upay/rahu/",
  "/remedies/for-dosha/rahu_dosha/": "/upay/rahu/",
  "/dosha/ketu-dosha/": "/upay/ketu/",
  "/remedies/for-dosha/ketu_dosha/": "/upay/ketu/",
  "/hi/monthly-rashifal/": "/hi/aaj-ka-rashifal/",
  "/hi/muhurat/naamkaran/": "/hi/muhurat/",
  "/collections/frontpage/": "/",
  "/collections/all/": "/upay/",
  "/collections/frontpage.atom": "/",
  "/collections/": "/",
  "/products/": "/",
  // Retired physical-product pages (Shopify catalog era, GSC 404 backlog 2026-07-24).
  // The two karungali products map to the surviving karungali editorial page; the
  // rest go home — there is no digital equivalent to inherit their equity.
  "/products/karungali-mala/":
    "/blogs/news/karungali-mala-benefits-what-tradition-and-science-say-about-black-ebony/",
  "/products/karungali-mala-1/":
    "/blogs/news/karungali-mala-benefits-what-tradition-and-science-say-about-black-ebony/",
  "/products/karungali-mala-siddh-rudraksha-bracelet-combo/":
    "/blogs/news/karungali-mala-benefits-what-tradition-and-science-say-about-black-ebony/",
  "/products/gold-plated-lord-ganesha-idol/": "/",
  "/products/om-pendant/": "/",
  "/products/rudraksha-bracelet-2/": "/",
  "/products/gold-plated-premium-rudraksha-chain-bracelet/": "/",
  "/products/rudraksha-mala-1/": "/",
  // The Hindi yoga hub never shipped (only /hi/yoga/<name>/ detail pages exist);
  // Google kept requesting the hub and got a 404 (GSC coverage 2026-07-24).
  "/hi/yoga/": "/yoga/",
  "/mahadasha/saturn/": "/mahadasha/shani/",
  "/nakshatra/punarvasu/pada-4/": "/nakshatra/punarvasu/",
  "/nakshatra/purva-bhadrapada/pada-4/": "/nakshatra/purva-bhadrapada/",
  "/yoga/dhana/": "/yoga/",
  "/yoga/viparita-raja/": "/yoga/",
  "/blogs/news/baby-names-by-nakshatra-complete-guide-for-2026/": "/baby-names/",
  "/blogs/news/sade-sati-2026-which-rashis-are-affected-and-what-to-do/": "/sade-sati/",
  "/blogs/news/free-kundali-how-ai-is-making-vedic-astrology-accessible-t/": "/free-kundali/",
  "/blogs/news/free-kundali-how-ai-is-making-vedic-astrology-accessible-to-everyone/":
    "/free-kundali/",
  "/blogs/news/saturn-transit-in-aries-2026-effects-on-all-12-rashis/": "/saturn-transit/",
  "/blogs/news/how-to-find-your-moon-sign-rashi-by-date-of-birth/": "/rashi-calculator/",
  "/blogs/news/kaal-sarp-dosha-signs-effects-and-proven-remedies/": "/kaal-sarp-dosha/",
  "/blogs/news/kundali-matching-beyond-36-gun-milan-what-really-matters/": "/kundli-matching/",
  "/blogs/news/kundli-matching-for-marriage-gun-milan-explained/": "/kundli-matching/",
  "/blogs/news/mangal-dosha-meaning-effects-and-remedies/": "/mangal-dosha-calculator/",
  "/blogs/news/what-is-kundli-complete-guide-to-vedic-birth-chart/": "/free-kundali/",
  "/blogs/news/nakshatra-the-27-lunar-mansions-of-vedic-astrology/": "/nakshatra/",
  "/pages/terms-and-conditions/": "/terms/",
  "/pages/privacy-policy/": "/privacy/",
  "/pages/delete-account/": "/privacy/",
  "/jupiter-transit-2026/": "/jupiter-transit/",
  "/rahu-transit-2026/": "/rahu-transit/",
  "/hi/jupiter-transit-2026/": "/hi/transits/",
  "/hi/rahu-transit-2026/": "/hi/transits/",
  "/kundali-milan/": "/kundli-matching/",
  "/kundli-milan/": "/kundli-matching/",
  "/hi/kundali-milan/": "/hi/kundli-matching/",
  "/hi/kundli-milan/": "/hi/kundli-matching/",
  "/lal-kitab-remedies/": "/lal-kitab/",
  "/hi/": "/hi/aaj-ka-rashifal/",
};
